import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/types';

export type ProfileUpsert = {
  display_name: string;
  handle: string;
  headline?: string | null;
  bio?: string | null;
  current_city?: string | null;
  current_country?: string | null;
  origin_city?: string | null;
  origin_country?: string | null;
  industry?: string | null;
  role_title?: string | null;
  company?: string | null;
  linkedin_url?: string | null;
  website_url?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  telegram_handle?: string | null;
  whatsapp?: string | null;
  languages?: string[];
  skills?: string[];
  interests?: string[];
  open_to?: Record<string, boolean>;
};

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

export type ProfileListItem = Pick<
  ProfileRow,
  | 'id'
  | 'handle'
  | 'display_name'
  | 'avatar_url'
  | 'headline'
  | 'role_title'
  | 'company'
  | 'current_city'
  | 'current_country'
  | 'industry'
  | 'status'
  | 'verification'
  | 'last_seen_at'
>;

export type MapProfile = {
  id: string;
  handle: string | null;
  display_name: string;
  role_title: string | null;
  current_city: string | null;
  current_country: string | null;
  lat: number;
  lng: number;
};

const LIST_COLS =
  'id,handle,display_name,avatar_url,headline,role_title,company,current_city,current_country,industry,status,verification,last_seen_at';

export type VerificationFilter = 'all' | 'members_only' | 'verified_only';

export type VerificationRequest = {
  id: string;
  requester_id: string;
  seed_profile_id: string | null;
  linkedin_url: string;
  note: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  review_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
};

export type ProfileFilters = {
  search?: string;
  industries?: string[];
  countries?: string[];
  verification?: VerificationFilter;
};

export const DIRECTORY_PAGE_SIZE = 24;

export function escapeIlike(input: string): string {
  // PostgREST .or() splits on commas and treats parentheses as grouping; escape
  // them so user-entered punctuation can't break the filter expression.
  return input.replace(/[,()]/g, ' ').trim();
}

export type ProfileSnippet = {
  id: string;
  display_name: string;
  handle: string | null;
};

export async function getProfileSnippet(profileId: string): Promise<ProfileSnippet | null> {
  const { data } = await supabase
    .from('profiles')
    .select('id, display_name, handle')
    .eq('id', profileId)
    .maybeSingle()
    .throwOnError();
  return data as ProfileSnippet | null;
}

export async function listProfiles(
  filters: ProfileFilters,
  page: number,
): Promise<{ items: ProfileListItem[]; hasMore: boolean }> {
  const from = page * DIRECTORY_PAGE_SIZE;
  const to = from + DIRECTORY_PAGE_SIZE - 1;

  let q = supabase
    .from('profiles')
    .select(LIST_COLS)
    .in('status', ['seeded', 'claimed', 'active'])
    .order('display_name', { ascending: true })
    .range(from, to);

  if (filters.industries && filters.industries.length > 0) {
    q = q.in('industry', filters.industries);
  }
  if (filters.countries && filters.countries.length > 0) {
    q = q.in('current_country', filters.countries);
  }
  if (filters.verification === 'members_only') {
    q = q.in('status', ['claimed', 'active']);
  } else if (filters.verification === 'verified_only') {
    q = q.in('verification', ['verified', 'admin_verified']);
  }
  if (filters.search && filters.search.trim().length > 0) {
    const term = `%${escapeIlike(filters.search)}%`;
    q = q.or(
      `display_name.ilike.${term},role_title.ilike.${term},current_city.ilike.${term}`,
    );
  }

  const { data, error } = await q;
  if (error) throw error;
  const items = (data ?? []) as ProfileListItem[];
  return { items, hasMore: items.length === DIRECTORY_PAGE_SIZE };
}

export async function listRandomPreview(
  count: number,
): Promise<ProfileListItem[]> {
  // Simple "random enough" preview — pull a batch ordered by created_at desc
  // and trim client-side. Good enough for the Landing page preview.
  const { data, error } = await supabase
    .from('profiles')
    .select(LIST_COLS)
    .in('status', ['seeded', 'claimed', 'active'])
    .order('created_at', { ascending: false })
    .limit(count * 3);
  if (error) throw error;
  const rows = (data ?? []) as ProfileListItem[];
  const shuffled = [...rows].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export async function getProfileByHandle(
  handle: string,
): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('handle', handle)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function listMapProfiles(
  limit = 1000,
): Promise<MapProfile[]> {
  // The `location` geography column serializes as WKB hex over PostgREST, which
  // is awkward to parse in-browser. The seed script always stashes the raw
  // lat/lng in `legacy_data`, so we read coords from there and keep the query
  // simple. Post-claim profiles without legacy_data will need a different
  // source (handled in P1d).
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id,handle,display_name,role_title,current_city,current_country,legacy_data',
    )
    .in('status', ['seeded', 'claimed', 'active'])
    .not('legacy_data', 'is', null)
    .limit(limit);
  if (error) throw error;

  const out: MapProfile[] = [];
  for (const row of data ?? []) {
    const legacy = row.legacy_data as
      | { lat?: number | null; lng?: number | null }
      | null;
    const lat = legacy?.lat;
    const lng = legacy?.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    out.push({
      id: row.id,
      handle: row.handle,
      display_name: row.display_name,
      role_title: row.role_title,
      current_city: row.current_city,
      current_country: row.current_country,
      lat,
      lng,
    });
  }
  return out;
}

export type ProfileStats = {
  members: number;
  cities: number;
  countries: number;
};

export async function getProfileStats(): Promise<ProfileStats> {
  const [{ count: members }, distinct] = await Promise.all([
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .in('status', ['seeded', 'claimed', 'active']),
    supabase
      .from('profiles')
      .select('current_city,current_country')
      .in('status', ['seeded', 'claimed', 'active']),
  ]);
  if (distinct.error) throw distinct.error;

  const cities = new Set<string>();
  const countries = new Set<string>();
  for (const row of distinct.data ?? []) {
    if (row.current_city) cities.add(row.current_city);
    if (row.current_country) countries.add(row.current_country);
  }
  return {
    members: members ?? 0,
    cities: cities.size,
    countries: countries.size,
  };
}

export async function listDistinctCountries(): Promise<string[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('current_country')
    .in('status', ['seeded', 'claimed', 'active'])
    .not('current_country', 'is', null);
  if (error) throw error;
  const set = new Set<string>();
  for (const row of data ?? []) {
    if (row.current_country) set.add(row.current_country);
  }
  return [...set].sort();
}

export async function getMyProfile(): Promise<ProfileRow | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('auth_user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function isHandleTaken(
  handle: string,
  excludeId?: string,
): Promise<boolean> {
  let q = supabase
    .from('profiles')
    .select('id', { count: 'exact', head: true })
    .eq('handle', handle);
  if (excludeId) q = q.neq('id', excludeId);
  const { count, error } = await q;
  if (error) throw error;
  return (count ?? 0) > 0;
}

export async function updateProfile(
  id: string,
  fields: ProfileUpsert,
): Promise<ProfileRow> {
  const { data, error } = await supabase
    .from('profiles')
    .update(fields)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'member'
  );
}

export async function createProfile(
  fields: ProfileUpsert,
): Promise<ProfileRow> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const base = fields.handle ? slugify(fields.handle) : slugify(fields.display_name);
  const payload = {
    ...fields,
    auth_user_id: user.id,
    status: 'active' as const,
    verification: 'unverified' as const,
  };

  // Retry up to 5 times if the generated handle is already taken.
  for (let attempt = 0; attempt < 5; attempt++) {
    const handle =
      attempt === 0
        ? base
        : `${base}-${Math.random().toString(36).slice(2, 6)}`;
    const { data, error } = await supabase
      .from('profiles')
      .insert({ ...payload, handle })
      .select('*')
      .single();
    if (!error) return data;
    // 23505 = unique_violation; retry on handle conflict only
    if ((error as { code?: string }).code !== '23505') throw error;
  }
  throw new Error('Could not generate a unique handle. Please try again.');
}

export async function uploadProfileMedia(
  userId: string,
  slot: 'avatar' | 'cover',
  file: File,
): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'jpg';
  const path = `${userId}/${slot}.${ext}`;
  const { error } = await supabase.storage
    .from('profile-media')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('profile-media').getPublicUrl(path);
  return data.publicUrl;
}

export async function submitVerificationRequest(
  linkedinUrl: string,
  note?: string,
  seedProfileId?: string,
): Promise<{ request_id: string }> {
  const { data, error } = await supabase.rpc('submit_verification_request', {
    p_linkedin_url:    linkedinUrl,
    p_note:            note ?? undefined,
    p_seed_profile_id: seedProfileId ?? undefined,
  });
  if (error) throw error;
  return data as { request_id: string };
}

export async function touchLastSeen(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase
    .from('profiles')
    .update({ last_seen_at: new Date().toISOString() })
    .eq('auth_user_id', user.id);
  // Fire-and-forget — presence writes must never block the user.
}

export async function getMyVerificationRequest(): Promise<VerificationRequest | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .maybeSingle();
  if (!profile) return null;
  const { data, error } = await supabase
    .from('verification_requests')
    .select('*')
    .eq('requester_id', profile.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as VerificationRequest | null;
}
